import {
  Component,
  Injector,
  OnDestroy,
  OnInit,
  ViewContainerRef,
  ChangeDetectorRef,
  ComponentRef,
} from '@angular/core';
import { environment } from '@env';
import { ILocation, IPropertyListModel, IPropertyMapModel } from '@interfaces';
import { select, Store } from '@ngrx/store';
import {
  GeoService,
  injectDestroyService,
  MapService,
  MapSettingService,
  OpenStreetMapService,
  provideDestroyService,
} from '@service';
import {
  AppState,
  selectMapCenter,
  selectMapZoom,
  selectPropertiesList,
  selectPropertiesMap,
  selectSearchItemSelected,
  selectSearchParams,
} from '@store';
import { LatLngBounds, Map, Marker } from 'leaflet';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { ModalMarkersInClusterComponent, ModalWhenClickLabelOnLabelComponent } from '..';
import * as FilterActions from '../../../store/actions/search-page.actions';
import { MoneyShortStringPipe, NumberToShortStringPipe } from '@pipes';
import { LocalStorageEnum } from '@enums';
import { removeNonUniquePartInsideAddressUtil } from '../../helpers/remove-non-unique-part-isnide-address.util';
import { LocalStorageService } from '../../services/local-storage.service';
import { generateFullAddress, getImageUrlThumb } from '@helpers';

@Component({
  selector: 'app-map',
  template: `
    <div id="map">
      <div class="spinner-large" [hidden]="loading"></div>
    </div>
  `,
  styleUrls: ['./map.component.scss'],
  providers: [provideDestroyService()],
  standalone: false,
})
export class MapComponent implements OnInit, OnDestroy {
  public map: Map;
  public marker: Marker;
  public loading = false;
  public clusters = this.mapService.L.markerClusterGroup(this.mapSetting.defaultStyleClusters);
  public propertyListCoordinates: IPropertyMapModel[] = [];
  public propertiesList: IPropertyListModel[] = [];

  private readonly destroy$ = injectDestroyService();

  markersArray = [];
  maxBounds: any;
  propertyInsideMapBounds = [];
  propertyListCoordinatesLocal;
  isOpenModalWithClusterMarkers = false;
  activeProperty;
  popupRef;
  zoomEndTimer;
  eventsAreCreated = false;
  mapMoveTimer;
  private popupComponentCache: { [key: string]: ComponentRef<any> } = {};
  private _previousBounds: LatLngBounds;
  private _lastZoom = 0;

  private readonly responseGetDistrict$: Subject<void>;
  private readonly responseGetDistrictCoordinate$: Subject<void>;
  private readonly mapMarkers$: Subject<AppState>;

  private isFirstInit = true;

  // Функция дебаунса для оптимизации событий карты
  private debounce(func: Function, wait: number): (...args: any[]) => void {
    let timeout: any;
    return (...args: any[]): void => {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  }

  constructor(
    public mapService: MapService,
    public mapSetting: MapSettingService,
    private osm: OpenStreetMapService,
    private store: Store,
    private viewContainerRef: ViewContainerRef,
    private numberToShortStringPipe: NumberToShortStringPipe,
    private moneyShortStringPipe: MoneyShortStringPipe,
    private ls: LocalStorageService,
    private injector: Injector,
    private geoService: GeoService,
    private cdr: ChangeDetectorRef,
  ) {
    this.responseGetDistrict$ = new Subject();
    this.responseGetDistrictCoordinate$ = new Subject();
    this.mapMarkers$ = new Subject();
  }

  ngOnInit() {
    if (this.mapService.L) {
      this.initWithParams();
      this.changeLoadingSpinner();
    }
  }

  getPropertyList() {
    this.changeCoordinatesRect();
    this.store.pipe(select(selectPropertiesMap), takeUntil(this.destroy$)).subscribe((res: IPropertyMapModel[]) => {
      const oldCount = this.propertyListCoordinates?.length || 0;
      const oldProperties = this.propertyListCoordinates || [];
      this.propertyListCoordinates = res;

      // Detect if we received new properties that weren't displayed before
      let hasNewProperties = oldCount === 0;

      if (!hasNewProperties && res.length !== oldCount) {
        // Check if we have completely new properties by ID
        const oldIds = new Set(oldProperties.map((p) => p.id));
        hasNewProperties = res.some((p) => !oldIds.has(p.id));
      }

      // Completely reset markers if it's the first load, or we have new properties to show
      if (hasNewProperties) {
        this.propertyListCoordinatesLocal = null; // Force marker rebuild
        this.setMarkersCluster();
      } else if (this.map && this.clusters) {
        // If a map is already showing and no complete reset needed, just update visible markers
        this.updateVisibleMarkers();
      }
    });

    // TODO: needed for only demo photos
    this.store.pipe(select(selectPropertiesList), takeUntil(this.destroy$)).subscribe((res: IPropertyListModel[]) => {
      this.propertiesList = res;
    });
  }

  setMarkersCluster() {
    if (this.hasUpdateMarkers()) {
      return;
    }

    if (!this.map) {
      return this.clusters.clearLayers();
    }

    if (this.propertyListCoordinates) {
      this.markersArray = [];
      this.propertyListCoordinatesLocal = this.propertyListCoordinates[0];
      this.maxBounds = this.map.getBounds();

      // Оптимизация: отображаем только те маркеры, которые видимы в текущих границах карты
      const bounds = this.map.getBounds();
      this.propertyInsideMapBounds = this.propertyListCoordinates.filter((item) =>
        bounds.contains([item.location.lat, item.location.long]),
      );

      this.createMarkers();

      if (this.map) {
        this.map.addLayer(this.createLayers());
      }
    }

    this.clusters.on('click', (event) => {
      const data = this.markersArray.find((item) => event.layer.options.className.includes(item.options.id))?.options;

      if (!data) {
        return;
      }

      if (!data.images?.length && environment.useDemoPhotos) {
        data.isDemoPhoto = true;
        const p = this.propertiesList.find((prop) => prop.id === data.id);
        const images = p?.images?.length ? p.images[0] : undefined;
        if (images) {
          data.images = [images];
        }
      }

      const popupSetting = {
        ...this.mapSetting.defaultSettingPopup,
        offset: this.calculateMarkerOffset(event.containerPoint, true),
        className: 'est-popup-image',
      };

      const popup = event.layer.bindPopup('', popupSetting);
      popup.setPopupContent(() => this.contentForPopupClickLabel(data));
      popup.openPopup();
      this.isOpenModalWithClusterMarkers = true;
      this.cdr.detectChanges();
    });

    this.clusters.on('clusterclick', (cluster: any) => {
      cluster.preventDefault;
      this.isOpenModalWithClusterMarkers = true;

      if (this.map.getZoom() >= 18) {
        const content = cluster.layer.getAllChildMarkers().map((item) => item.options);

        if (environment.useDemoPhotos) {
          content.forEach((item) => {
            if (!item.images?.length) {
              item.isDemoPhoto = true;
              const p = this.propertiesList.find((prop) => prop.id === item.id);
              const images = p?.images?.length ? p.images[0] : undefined;
              if (images) {
                item.images = [images];
                item.mainImage = `${getImageUrlThumb(p.mainImage)}`;
              }
            }
          });
        }

        const popupSetting = {
          ...this.mapSetting.defaultSettingPopup,
          offset: this.calculateMarkerOffset(cluster.containerPoint, false),
          className: 'modal-cluster',
          maxWidth: 0,
          autoPan: false,
        };
        const popup = cluster.layer.bindPopup('', popupSetting);
        content?.forEach((item) => {
          item.fullAddress = generateFullAddress(item);
        });
        popup.setPopupContent((_) => this.contentForPopupSeveralPropertyWithOneAddress(content));
        popup.openPopup();
        popup.on('popupclose', (_) => {
          this.isOpenModalWithClusterMarkers = false;
          this.cdr.detectChanges();
        });
        this.cdr.detectChanges();
      }
    });
  }

  calculateMarkerOffset(markerPos: { x: number; y: number }, isSingle: boolean): number[] {
    const res = isSingle ? [0, 50] : [0, 10];
    const popupWidth = isSingle ? 210 : 420;
    const popupHeight = isSingle ? 140 : 270;
    const mapSize = this.map.getSize();

    if (markerPos.x < popupWidth / 2) {
      res[0] = popupWidth / 2 - markerPos.x;
    } else if (markerPos.x > mapSize.x - popupWidth / 2) {
      res[0] = mapSize.x - markerPos.x - popupWidth / 2;
    }

    if (markerPos.y < popupHeight) {
      res[1] = -1 * (markerPos.y - popupHeight - 30);
    }
    return res;
  }

  hasUpdateMarkers(): boolean {
    // Return true if we don't need to update markers
    // We need to update if:
    // 1. propertyListCoordinatesLocal is null (forced update)
    // 2. propertyListCoordinates is different than what we last processed
    return (
      this.propertyListCoordinatesLocal !== null &&
      this.propertyListCoordinates &&
      this.propertyListCoordinatesLocal === this.propertyListCoordinates[0]
    );
  }

  showActiveProperty(item: IPropertyListModel) {
    if (this.activeProperty?.id === item.id) {
      return;
    }

    this.activeProperty = item;
    const selectEl = this.markersArray.find((m) => m.options.id === item.id);

    if (selectEl) {
      this.marker = this.mapService.L.marker(
        this.mapService.L.latLng(selectEl.options.location.lat, selectEl.options.location.long),
      );

      this.map.addLayer(this.marker);
      const data = { ...selectEl.options, images: [item.images[0]], isDemoPhoto: item.isDemoPhoto };

      const parentSelectEl = this.clusters.getVisibleParent(selectEl);
      if (!parentSelectEl) {
        return;
      }

      const pixelPoint = this.map.latLngToContainerPoint(parentSelectEl.getLatLng());

      const popupSetting = {
        ...this.mapSetting.defaultSettingPopup,
        offset: this.calculateMarkerOffset(pixelPoint, true),
        className: 'est-popup-image',
        width: 201,
      };
      // @ts-ignore
      this.popupRef = parentSelectEl.bindPopup('', popupSetting);

      this.popupRef.setPopupContent((_) => this.contentForPopupClickLabel(data));
      this.popupRef.openPopup();
    }
  }

  clearActiveProperty(): void {
    if (this.popupRef) {
      this.activeProperty = undefined;
      this.popupRef.closePopup();
      this.popupRef = undefined;
    }
  }

  createMarkers() {
    this.propertyInsideMapBounds.forEach((item: IPropertyMapModel) => {
      // @ts-ignore
      const cluster = this.mapService.L.marker({ lng: item.location.long, lat: item.location.lat }, { ...item });
      let priceLabel = '00K';

      priceLabel = `${item.priceSymbol} ${this.moneyShortStringPipe.transform(item.price)}`;

      cluster.bindTooltip(priceLabel, {
        permanent: true,
        direction: 'top',
        className: ['label-marker', item.id].join(' '),
        interactive: true,
      });
      cluster.openTooltip();
      this.markersArray.push(cluster);
    });
    this.adjustOverlappingMarkers(this.markersArray, 0.00015); // Примерное минимальное расстояние
  }

  /**
   * Adjusts markers if they overlap each other (by coordinates)
   * @param markers array of leaflet markers
   * @param minDistance minimum distance between centers (in degrees)
   */
  private adjustOverlappingMarkers(markers: any[], minDistance: number) {
    for (let i = 0; i < markers.length; i++) {
      for (let j = i + 1; j < markers.length; j++) {
        const m1 = markers[i];
        const m2 = markers[j];
        const latlng1 = m1.getLatLng();
        const latlng2 = m2.getLatLng();
        const dx = latlng1.lat - latlng2.lat;
        const dy = latlng1.lng - latlng2.lng;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < minDistance) {
          // Move m2 in a circle around m1
          const angle = Math.atan2(dy, dx) + Math.PI / 4; // slightly diagonally
          const newLat = latlng1.lat + Math.cos(angle) * minDistance;
          const newLng = latlng1.lng + Math.sin(angle) * minDistance;
          m2.setLatLng([newLat, newLng]);
        }
      }
    }
  }

  iconCreateFunction(cluster) {
    return this.mapService.L.divIcon({
      html: `<div><span>${this.numberToShortStringPipe.transform(cluster.getChildCount())}</span></div>`,
      className: `marker-cluster marker-cluster-large`,
      iconSize: this.mapService.L.point(40, 40),
    });
  }

  createLayers() {
    this.clusters.clearLayers();

    let levelDefault;
    const currentZoom = this.map.getZoom();

    // Адаптируем максимальный радиус кластера в зависимости от зума
    const maxClusterRadius = Math.max(30, 120 - (currentZoom - 10) * 10);

    if (currentZoom <= 17) {
      this.clusters = this.mapService.L.markerClusterGroup({
        ...this.mapSetting.defaultStyleClusters,
        maxClusterRadius,
        showCoverageOnHover: false,
        chunkedLoading: true,
        chunkInterval: 50,
        chunkDelay: 10,
        iconCreateFunction: this.iconCreateFunction.bind(this),
        animate: true,
      });
      levelDefault = this.clusters.addLayers(this.markersArray);
    }

    if (currentZoom >= 18) {
      this.clusters = this.mapService.L.markerClusterGroup({
        maxClusterRadius: 70,
        spiderfyOnMaxZoom: false,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: false,
        chunkedLoading: true,
        chunkInterval: 50,
        chunkDelay: 10,
        iconCreateFunction: this.iconCreateFunction.bind(this),
        animate: true,
      });
      levelDefault = this.clusters.addLayers(this.markersArray);
    }

    // Смещаем кластеры, если они накладываются на маркеры
    this.adjustClustersFromMarkers();

    return levelDefault;
  }

  /**
   * Adjusts clusters if they overlap with markers
   * (works after adding clusters and markers to the map)
   */
  private adjustClustersFromMarkers() {
    // Get DOM elements of clusters and markers
    const clusterElements = document.querySelectorAll('.marker-cluster');
    const markerElements = document.querySelectorAll('.leaflet-marker-icon:not(.marker-cluster)');
    const minDistance = 40; // px, minimum distance between centers

    clusterElements.forEach((clusterEl) => {
      const clusterRect = (clusterEl as HTMLElement).getBoundingClientRect();
      markerElements.forEach((markerEl) => {
        const markerRect = (markerEl as HTMLElement).getBoundingClientRect();
        const dx = clusterRect.left + clusterRect.width / 2 - (markerRect.left + markerRect.width / 2);
        const dy = clusterRect.top + clusterRect.height / 2 - (markerRect.top + markerRect.height / 2);
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < minDistance) {
          // Move cluster by minDistance away from marker
          const angle = Math.atan2(dy, dx);
          const offsetX = Math.cos(angle) * (minDistance - distance);
          const offsetY = Math.sin(angle) * (minDistance - distance);
          const style = (clusterEl as HTMLElement).style;
          style.transform = `translate(${offsetX}px, ${offsetY}px)`;
        }
      });
    });
  }

  changeCoordinatesRect() {
    const bounds: LatLngBounds = this.map.getBounds();

    // Не сравниваем границы, так как это может блокировать отправку запросов
    // Вместо этого используем debounce через setTimeout

    const coordinatesRect = {
      topLat: bounds.getNorthWest().lat,
      topLong: bounds.getNorthWest().lng,
      botLat: bounds.getSouthEast().lat,
      botLong: bounds.getSouthEast().lng,
    };
    const { lat, lng } = this.map.getCenter();
    const mapCenter: ILocation = { lat, long: lng };
    const zoom = this.map.getZoom();

    clearTimeout(this.zoomEndTimer);
    this.zoomEndTimer = setTimeout(() => {
      this.mapService.updateInitParams({ zoom, coordinatesRect, mapCenter });
      this.store.dispatch(FilterActions.changeCoordinatesRect({ coordinatesRect }));
      this.store.dispatch(FilterActions.saveMapCenter({ mapCenter, zoom }));
    }, 100);
  }

  // Debounced functions for handling map events
  private handleZoomEnd = this.debounce(() => {
    this.map.addLayer(this.createLayers());
    this.changeCoordinatesRect();
  }, 100);

  private handleMoveEnd = this.debounce(() => {
    this.map.addLayer(this.createLayers());
    this.changeCoordinatesRect();
    this.mapService.updateInitParams({ searchItemSelected: undefined });
  }, 100);

  createMapEvents() {
    // Запоминаем начальный зум карты
    this._lastZoom = this.map.getZoom();

    // Инициализируем слой кластеров один раз
    const currentZoom = this.map.getZoom();

    // Настройка кластеров в зависимости от зума
    if (currentZoom <= 17) {
      const maxClusterRadius = Math.max(30, 120 - (currentZoom - 10) * 10);

      this.clusters = this.mapService.L.markerClusterGroup({
        ...this.mapSetting.defaultStyleClusters,
        maxClusterRadius,
        showCoverageOnHover: false,
        chunkedLoading: true,
        chunkInterval: 50,
        chunkDelay: 10,
        iconCreateFunction: this.iconCreateFunction.bind(this),
        animate: false, // Отключаем анимацию для лучшей производительности
      });
    } else {
      this.clusters = this.mapService.L.markerClusterGroup({
        maxClusterRadius: 0,
        spiderfyOnMaxZoom: false,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: false,
        chunkedLoading: true,
        chunkInterval: 50,
        iconCreateFunction: this.iconCreateFunction.bind(this),
        animate: false,
      });
    }

    // Добавляем кластеры на карту один раз
    this.createMarkers();
    this.clusters.addLayers(this.markersArray);
    this.map.addLayer(this.clusters);

    // При изменении зума карты обновляем только настройки кластеризации, но не пересоздаем все маркеры
    this.map.on('zoomstart', () => {
      // При зуммировании нам не нужно удалять кластеры, только настраивать их отображение
      this.handleClusterVisibility(true);
    });

    this.map.on('zoomend', () => {
      // Обновляем координаты и настройки кластеров без пересоздания
      this.handleClusterVisibility(false);
      this.changeCoordinatesRect();
    });

    this.map.on('movestart', () => {
      // При перемещении карты также скрываем кластеры для плавности
      this.handleClusterVisibility(true);
    });

    this.map.on('moveend', () => {
      // После завершения движения обновляем видимые маркеры и отображаем кластеры
      this.handleClusterVisibility(false);

      // Явно вызываем метод для обновления координат и отправки запросов
      this.changeCoordinatesRect();

      this.mapService.updateInitParams({ searchItemSelected: undefined });
    });

    this.map.on('click', () => {
      this.isOpenModalWithClusterMarkers = false;
    });

    // Настраиваем обработчики событий кластеров
    this.setupClusterEvents();
  }

  // New method for managing cluster visibility - modified to eliminate flickering
  private handleClusterVisibility(isMoving: boolean) {
    if (isMoving) {
      // Instead of completely hiding, make semi-transparent and add slight blur
      const clusterElements = document.querySelectorAll('.marker-cluster, .leaflet-marker-icon');
      clusterElements.forEach((el) => {
        const element = el as HTMLElement;
        element.style.opacity = '0.6'; // Make semi-transparent but not completely hidden
        element.style.transition = 'opacity 0.1s ease-out'; // Quick transition
        element.style.filter = 'blur(1px)'; // Light blur to indicate "movement"
      });
    } else {
      const currentZoom = this.map.getZoom();

      // Always update markers when map stops moving or zooming
      this.updateVisibleMarkers();

      // Save current zoom for next comparison
      this._lastZoom = currentZoom;

      // Return to normal view after movement ends
      requestAnimationFrame(() => {
        const clusterElements = document.querySelectorAll('.marker-cluster, .leaflet-marker-icon');
        clusterElements.forEach((el) => {
          const element = el as HTMLElement;
          element.style.opacity = '1';
          element.style.transition = 'opacity 0.15s ease-in'; // Smooth transition to full visibility
          element.style.filter = 'none'; // Remove blur
        });
      });
    }
  }

  // Optimized marker creation without completely removing existing ones
  private updateVisibleMarkers() {
    const currentZoom = this.map.getZoom();
    const bounds = this.map.getBounds();
    const significantZoomChange = Math.abs(this._lastZoom - currentZoom) >= 2;

    // Create extended bounds with buffer for preloading markers outside visible area
    const bufferFactor = 0.5; // 50% additional space around visible area
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    const latDiff = northEast.lat - southWest.lat;
    const lngDiff = northEast.lng - southWest.lng;

    const extendedBounds = this.mapService.L.latLngBounds(
      [southWest.lat - latDiff * bufferFactor, southWest.lng - lngDiff * bufferFactor],
      [northEast.lat + latDiff * bufferFactor, northEast.lng + lngDiff * bufferFactor],
    );

    // Get markers in extended area (with buffer)
    const visibleProperties =
      this.propertyListCoordinates?.filter((item) =>
        extendedBounds.contains([item.location.lat, item.location.long]),
      ) || [];

    // Check which markers already exist
    const existingMarkerIds = new Set(this.markersArray.map((m) => m.options.id));
    const newMarkers = [];

    // Create only new markers that don't exist yet
    visibleProperties.forEach((item: IPropertyMapModel) => {
      if (!existingMarkerIds.has(item.id)) {
        const marker = this.mapService.L.marker({ lng: item.location.long, lat: item.location.lat }, {
          ...item,
        } as any);

        const priceLabel = `${item.priceSymbol} ${this.moneyShortStringPipe.transform(item.price)}`;

        marker.bindTooltip(priceLabel, {
          permanent: true,
          direction: 'top',
          className: ['label-marker', item.id].join(' '),
          interactive: true,
        });

        marker.openTooltip();
        // @ts-ignore - temporary timestamp for marker lifecycle management
        marker.options._addedTimestamp = Date.now();
        newMarkers.push(marker);
      }
    });

    // Smart strategy for managing existing markers
    const now = Date.now();
    const maxMarkerCount = 1000; // Maximum count to avoid memory leaks

    // Filter existing markers - keep markers in extended area and recently added ones
    const filteredExistingMarkers = this.markersArray.filter((m) => {
      const isInExtendedBounds = visibleProperties.some((p) => p.id === m.options.id);
      const wasRecentlyAdded = m.options._addedTimestamp && now - m.options._addedTimestamp < 10000;
      return isInExtendedBounds || wasRecentlyAdded;
    });

    // Add timestamp to new markers
    newMarkers.forEach((m) => {
      // @ts-ignore - temporary timestamp for marker lifecycle management
      m.options._addedTimestamp = now;
    });

    // Combine and limit total number of markers
    this.markersArray = [...filteredExistingMarkers, ...newMarkers].slice(0, maxMarkerCount);

    // Only recreate clusters on significant zoom changes
    if (significantZoomChange) {
      // Temporarily save current markers to avoid flickering
      const oldClusters = this.clusters;

      // Create new clusters with settings for current zoom
      if (currentZoom <= 17) {
        const maxClusterRadius = Math.max(30, 120 - (currentZoom - 10) * 10);
        this.clusters = this.mapService.L.markerClusterGroup({
          ...this.mapSetting.defaultStyleClusters,
          maxClusterRadius,
          showCoverageOnHover: false,
          chunkedLoading: true,
          chunkInterval: 50,
          chunkDelay: 10,
          iconCreateFunction: this.iconCreateFunction.bind(this),
          animate: false,
        });
      } else {
        this.clusters = this.mapService.L.markerClusterGroup({
          maxClusterRadius: 0,
          spiderfyOnMaxZoom: false,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: false,
          chunkedLoading: true,
          chunkInterval: 50,
          iconCreateFunction: this.iconCreateFunction.bind(this),
          animate: false,
        });
      }

      // Add all markers to new clusters
      this.clusters.addLayers(this.markersArray);
      this.map.addLayer(this.clusters);

      // Set up event handlers for new clusters
      this.setupClusterEvents();

      // Remove old clusters only after adding new ones
      setTimeout(() => {
        this.map.removeLayer(oldClusters);
      }, 50);
    } else {
      // For regular panning, just add new markers to existing clusters
      // First, clear existing clusters to ensure all markers are correctly shown
      this.clusters.clearLayers();
      // Then add all markers back to ensure everything is visible
      this.clusters.addLayers(this.markersArray);
    }

    // Apply marker positioning adjustments if needed
    if (newMarkers.length > 5) {
      this.adjustOverlappingMarkers(newMarkers, 0.00015);
    }
  }

  // Вынесем обработчики событий кластеров в отдельный метод
  private setupClusterEvents() {
    this.clusters.on('click', (event) => {
      const data = this.markersArray.find((item) => event.layer.options.className.includes(item.options.id))?.options;

      if (!data) {
        return;
      }

      if (!data.mainImage?.length && environment.useDemoPhotos) {
        data.isDemoPhoto = true;
        const p = this.propertiesList.find((prop) => prop.id === data.id);
        const images = p?.images?.length ? p.images[0] : undefined;
        if (images) {
          data.images = [images];
        }
      }

      const popupSetting = {
        ...this.mapSetting.defaultSettingPopup,
        offset: this.calculateMarkerOffset(event.containerPoint, true),
        className: 'est-popup-image',
      };

      const popup = event.layer.bindPopup('', popupSetting);
      popup.setPopupContent(() => this.contentForPopupClickLabel(data));
      popup.openPopup();
      this.isOpenModalWithClusterMarkers = true;
      this.cdr.detectChanges();
    });

    this.clusters.on('clusterclick', (cluster: any) => {
      cluster.preventDefault;
      this.isOpenModalWithClusterMarkers = true;

      if (this.map.getZoom() >= 18) {
        const content = cluster.layer.getAllChildMarkers().map((item) => item.options);

        if (environment.useDemoPhotos) {
          content.forEach((item) => {
            if (!item.mainImage?.length) {
              item.isDemoPhoto = true;
              const p = this.propertiesList.find((prop) => prop.id === item.id);
              const images = p?.images?.length ? p.images[0] : undefined;
              if (images) {
                item.images = [images];
                item.mainImage = `${environment.imgBucketUrl}${item.images[0]}`;
              }
            }
          });
        }

        const popupSetting = {
          ...this.mapSetting.defaultSettingPopup,
          offset: this.calculateMarkerOffset(cluster.containerPoint, false),
          className: 'modal-cluster',
          maxWidth: 0,
          autoPan: false,
        };
        const popup = cluster.layer.bindPopup('', popupSetting);
        content?.forEach((item) => {
          item.fullAddress = generateFullAddress(item);
        });
        popup.setPopupContent((_) => this.contentForPopupSeveralPropertyWithOneAddress(content));
        popup.openPopup();
        popup.on('popupclose', (_) => {
          this.isOpenModalWithClusterMarkers = false;
          this.cdr.detectChanges();
        });
        this.cdr.detectChanges();
      }
    });
  }

  contentForPopupSeveralPropertyWithOneAddress(markers) {
    // Use component cache for reuse
    const cacheKey = 'cluster_' + markers.map((m) => m.id).join('_');

    if (this.popupComponentCache[cacheKey]) {
      const componentRef = this.popupComponentCache[cacheKey];
      componentRef.instance.markers = markers.map((item) => ({
        ...item,
        fullAddress: removeNonUniquePartInsideAddressUtil(item.fullAddress),
      }));
      componentRef.changeDetectorRef.detectChanges();
      return componentRef.location.nativeElement;
    }

    const componentRef = this.viewContainerRef.createComponent(ModalMarkersInClusterComponent);
    componentRef.instance.markers = markers.map((item) => ({
      ...item,
      fullAddress: removeNonUniquePartInsideAddressUtil(item.fullAddress),
    }));
    componentRef.changeDetectorRef.detectChanges();

    // Save component in cache
    this.popupComponentCache[cacheKey] = componentRef;

    return componentRef.location.nativeElement;
  }

  contentForPopupClickLabel(marker) {
    // Use component cache for reuse
    if (this.popupComponentCache[marker.id]) {
      const componentRef = this.popupComponentCache[marker.id];
      componentRef.instance.marker = marker;
      componentRef.changeDetectorRef.detectChanges();
      return componentRef.location.nativeElement;
    }

    const componentRef = this.viewContainerRef.createComponent(ModalWhenClickLabelOnLabelComponent);
    componentRef.instance.marker = marker;
    componentRef.changeDetectorRef.detectChanges();

    // Save component in cache
    this.popupComponentCache[marker.id] = componentRef;

    return componentRef.location.nativeElement;
  }

  public clearClusterOverlay(): void {
    // Используем requestAnimationFrame для оптимизации работы с DOM
    requestAnimationFrame(() => {
      document
        .querySelectorAll('.leaflet-overlay-pane > .leaflet-zoom-animated > g > path[fill-opacity="0.4"]')
        ?.forEach((el) => el.remove());
    });
  }

  public initWithParams(): void {
    combineLatest([
      this.store.pipe(select(selectSearchParams)),
      this.store.pipe(select(selectSearchItemSelected)),
      this.store.pipe(select(selectMapCenter)),
      this.store.pipe(select(selectMapZoom)),
    ])
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((oldVal, newVal) => {
          return JSON.stringify(oldVal[2]) === JSON.stringify(newVal[2]);
        }),
      )
      .subscribe(([searchParams, searchItemSelected, mapCenter, zoom]) => {
        if (mapCenter || searchItemSelected) {
          const lat = mapCenter ? `${mapCenter.lat}` : searchItemSelected.lat;
          const lon = mapCenter ? `${mapCenter.long}` : searchItemSelected.lon;
          this.initMap(lat, lon, zoom);
        } else if (searchParams) {
          this.getDistrictCoordinateByName(searchParams.name || searchParams.city);
        } else {
          if (!this.ls.getItem(LocalStorageEnum.GeoUserInfo)) {
            this.geoService
              .getUserInfo()
              .pipe(switchMap((res) => this.geoService.getCityUser(res.headers.get('CF-Connecting-IP'))))
              .subscribe((res) => {
                this.ls.setItem(LocalStorageEnum.GeoUserInfo, res.body);
                const [lat, lon] = this.ls.getItem(LocalStorageEnum.GeoUserInfo).loc.split(',');
                this.initMap(lat, lon);
              });
          } else {
            const [lat, lon] = this.ls.getItem(LocalStorageEnum.GeoUserInfo).loc.split(',');
            this.initMap(lat, lon);
          }
        }
      });

    this.store.pipe(select(selectSearchItemSelected), takeUntil(this.destroy$)).subscribe((res) => {
      this.mapService.removeMarker(this.mapService.marker);
      if (res) {
        this.mapService.flyTo(+res.lat, +res.lon, this.mapSetting.defaultZoomMap);
        this.store.dispatch(FilterActions.clearFoundAddressItem());
        setTimeout(() => {
          this.mapService.marker = this.mapService.addMarker(+res.lat, +res.lon);
        }, 100);
      }
    });
  }

  changeLoadingSpinner() {
    this.loading = !this.loading;
  }

  getDistrictCoordinateByName(name: string): void {
    this.osm
      .getDistrictCoordinate(name)
      .pipe(takeUntil(this.responseGetDistrictCoordinate$))
      .subscribe((res) => {
        // @ts-ignore
        const [addressObj] = res;
        const { lon, lat } = addressObj;
        this.initMap(lat, lon);
      });
  }

  initMap(lat: string, lon: string, zoom = this.mapSetting.defaultZoomMap): void {
    this.clearClusterOverlay();
    if (!this.map) {
      this.map = this.mapService.setupMap({ lat, lon, zoom });
      if (!this.eventsAreCreated) {
        setTimeout(() => this.createMapEvents());
        this.eventsAreCreated = true;
      }
      this.getPropertyList();
    } else {
      this.mapService.flyTo(+lat, +lon);
    }
    //this.setMarkersCluster();
    setTimeout(() => {
      if (this.isFirstInit) {
        this.isFirstInit = false;
        this.map.invalidateSize();
        this.mapService.tileLayer.redraw();
        return;
      }
    }, 100);
  }

  ngOnDestroy(): void {
    // Clean up component cache and resources
    Object.values(this.popupComponentCache).forEach((componentRef) => {
      componentRef.destroy();
    });
    this.popupComponentCache = {};

    this.markersArray = [];
    this.clusters.clearLayers();

    this.map.off('zoomend');
    this.map.off('moveend');
    this.responseGetDistrict$.next();
    this.responseGetDistrictCoordinate$.next();
    this.mapMarkers$.next(undefined);
  }
}
